// dependencies
import * as Sentry from "@sentry/react";
import React from "react";

// schema
import { Text } from "@performyard/py-components";

import { AdminContainer, AdminTitle } from "../../../../components/Admin";
//components
import { Grid, GridCol } from "../../../../components/Grid";
import Toggle from "../../../../components/Inputs/ToggleSwitches/ThumbSwitch";
import PageTitle from "../../../../components/PageTitle";
import Spacer from "../../../../components/Spacer";

// hooks
import { useAiSettings, useUpdateAiSettings } from "../../../../api/hooks";
import { getFeatureFlagQuery, useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";
import useToast from "../../../../hooks/useToast";

import { companyAISettingsQuery, currentUserQuery } from "../../../../api/queries";
import { dataLoader } from "../../../../api/queries/dataLoader";

// utils
import { showError } from "../../../../utils/RequestReponse";

// pages
import ErrorPage from "../../../Error";

/** Data loader function */
export const aIEngagementAnalysisSettingsLoader = () => {
	const queries = [companyAISettingsQuery, getFeatureFlagQuery];

	return dataLoader([[currentUserQuery], queries]);
};

/** Page content */
const AIEngagementAnalysisSettings = () => {
	const { mutateAsync: updateAiSettings } = useUpdateAiSettings();
	const { data } = useAiSettings();
	// ai feature flag enabled
	const { value: aiFeatureFlagEnabled } = useFeatureFlagQuery("ff-is-ai-enabled", false);

	const toast = useToast();

	const [engagementAnalysisEnabled, setEngagementAnalysisEnabled] = React.useState(data?.engagementAnalysisEnabled);
	const [cohortInsightsEnabled, setCohortInsightsEnabled] = React.useState(data?.engagementCohortInsightsEnabled);

	// toggles behave differently in that changes are saved without having to click the save button
	const handleToggleTextResponseSummarization = async (val: boolean) => {
		try {
			await updateAiSettings({ engagementAnalysisEnabled: val });
			setEngagementAnalysisEnabled(val);
			toast(`Enable Text Response Summarization ${val === true ? "Enabled" : "Disabled"}.`, {
				type: "success",
				size: "large",
			});
		} catch {
			showError("Unable to save settings.");
		}
	};

	const handleToggleCohortInsights = async (val: boolean) => {
		try {
			await updateAiSettings({ engagementCohortInsightsEnabled: val });
			setCohortInsightsEnabled(val);
			toast(`Enable Cohort Insights ${val === true ? "Enabled" : "Disabled"}.`, {
				type: "success",
				size: "large",
			});
		} catch {
			showError("Unable to save settings.");
		}
	};

	if (!aiFeatureFlagEnabled) {
		return <ErrorPage code={404} />;
	}

	return (
		<Sentry.ErrorBoundary fallback={undefined}>
			<Spacer height={4} />
			<AdminContainer>
				<PageTitle title="AI Engagement Analysis Settings" />
				<AdminTitle>AI Engagement Analysis</AdminTitle>
				<Spacer height={4} />
				<Grid cols={10} gap={2}>
					{/* left side of the page */}
					<GridCol span={4}>
						<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
							Engagement Analysis
						</Text>
						<Spacer height={1} />
						<div className={"max-w-[365px]"}>
							<Text size="paragraph-3" weight="regular" color="neutral-9">
								Text response summarization allows admins to receive a report containing a summary and
								analysis of responses to text questions in a pulse survey.
							</Text>
						</div>
						<Spacer height={3} />
					</GridCol>
					<GridCol span={1} />

					{/* right side of the page */}
					<GridCol span={5}>
						<Grid cols={5}>
							<GridCol span={3}>
								<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
									Enable Text Response Summarization
								</Text>
								<Spacer height={1} />
								<div className="max-w-[300px]">
									<Text size="paragraph-3" weight="regular" color="neutral-9">
										Disabling this setting will hide the ability to summarize text responses in
										Engagement.
									</Text>
								</div>
							</GridCol>

							<GridCol span={2}>
								<div data-testid="text-response-summarization-toggle" className="text-right">
									<div className="inline-block">
										<Toggle
											onChange={(val) => handleToggleTextResponseSummarization(val)}
											value={engagementAnalysisEnabled || false}
										/>
									</div>
								</div>
							</GridCol>
							<GridCol span={3}>
								<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
									Enable Cohort Analysis
								</Text>
								<Spacer height={1} />
								<div className="max-w-[300px]">
									<Text size="paragraph-3" weight="regular" color="neutral-9">
										Disabling this setting will hide the ability to analyze cohort satisfaction
										scores on the Cohorts dashboard page.
									</Text>
								</div>
							</GridCol>

							<GridCol span={2}>
								<div data-testid="cohort-insights-toggle" className="text-right">
									<div className="inline-block">
										<Toggle
											onChange={(val) => handleToggleCohortInsights(val)}
											value={cohortInsightsEnabled || false}
										/>
									</div>
								</div>
							</GridCol>
						</Grid>
					</GridCol>
				</Grid>
			</AdminContainer>
		</Sentry.ErrorBoundary>
	);
};

export default AIEngagementAnalysisSettings;
